import { skipToken } from '@reduxjs/toolkit/query';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  useSmallScreen,
  useWindowDimensions,
} from '@/hooks/useWindowDimension';
import { useListHeight } from '@/hooks/useListHeight';
import { useScrolled } from '@/hooks/useScrolled';
import {
  setActiveGroupName,
  setListView,
  setEncroachmentThresholds,
  setPriorityThresholds,
  setCriticalTreesThresholds,
  setClearThresholds,
  setFilterBoundingBox,
  updateViewState,
  saveViewStateBeforeDrill,
  restoreViewStateBeforeDrill,
  clearItem,
  setWildfirePriorityThresholds,
  setGroupDetailTab,
  type GroupDetailTab,
} from '@/features/analysisMapSlice';
import { GroupAnalysisType } from '@/generated/types/navigator/types.gen';
import {
  Box,
  Flex,
  Group,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { TabsComponent } from '@/components/Tabs/Tabs';
import { GroupRiskSummary } from '@/views/list/level3/GroupRiskSummary';
import { MapListToggle } from '@/views/list/MapListToggle';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { FilterState } from '@/features/filterSlice';
import { AnalysisExport } from '@/views/analysis/AnalysisExport';
import { DEFAULT_CATEGORY_THRESHOLD } from '@/app/utils';
import { DetailViewHeader } from '@/views/list/detail/DetailViewHeader';
import TagManager from 'react-gtm-module';
import { GoogleEvents } from '@/features/googleEvents';
import { useTheme } from '@/assets/styles/themeMantine';
import { ChevronLeft } from 'react-feather';
import classes from './AnalysisList.module.css';
import { FeatureBar } from '@/views/feature-bar/FeatureBar';
import { GroupListComponent } from '@/views/list/GroupListComponent';
import { ListSummary } from '@/views/list/ListSummary';
import type { RedFlagWarning } from '@/features/wildfireSlice';
import { useNavigateWithSearch } from '@/hooks/useNavigateWithSearch';
import { useCurrentRootPath } from '@/views/useUrlSync';
import {
  useFilterGroupSummaryAnalysisQuery,
  useFilterNetworkGroupAnalysisQuery,
} from '@/features/analysisApi';
import { type WebMercatorViewportOptions } from '@/views/map/types';

import {
  DESKTOP_VIEWBOX_PADDING,
  MOBILE_VIEWBOX_PADDING,
  updateViewport,
} from '@/views/map/deckglUtils';
import isEqual from 'lodash/isEqual';
import { filterFalsyValues } from '@/views/filter/utils';
import ListPane from '@/views/list/ListPane';
import { ObjectiveDropdown } from '@/views/list/analysisList/ObjectiveDropdown';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import { useCustomListSelectorModal } from '@/views/list/custom-lists/useCustomListSelectorModal';
import { BulkActionDropdown } from '@/views/list/BulkActionDropdown';
import { useShouldShowBulkActions } from '@/views/list/BulkActionDropdown.utils';
import { useAddToList } from '@/views/list/analysisList/useAddToList';
import { useHasGroupRiskSummary } from '@/hooks/useWildfireMode';
import {
  openWorklogModal,
  WorklogModalMode,
} from '@/features/worklogModalSlice';

interface AnalysisListProps {
  shouldShowRedFlagWarnings?: boolean;
  setRedFlagWarnings: React.Dispatch<
    React.SetStateAction<RedFlagWarning[] | undefined>
  >;
}

export const AnalysisList = ({
  shouldShowRedFlagWarnings,
  setRedFlagWarnings,
}: AnalysisListProps) => {
  const dispatch = useAppDispatch();
  const {
    isListView,
    selectedAnalysis,
    selectedNetworkGroup,
    activeGroupName,
    spanNetworkGroup,
    viewState,
    groupDetailTab,
  } = useAppSelector((state) => state.analysisMap);
  const layerConfig = useAppSelector((s) => s.mapLayer);
  const theme = useTheme();
  const intl = useClientTerminology();
  const { modalComponent, openModal } = useCustomListSelectorModal();
  const isSmallScreen = useSmallScreen();
  const hasGroupRiskSummary = useHasGroupRiskSummary();
  const { width } = useWindowDimensions();
  const filterQuery = useAppSelector((state) => state.filterQuery);

  const [showExportModal, setShowExportModal] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const switcher = useRef<HTMLDivElement>(null);
  const [isListStackScrolled, listStackRef] = useScrolled<HTMLDivElement>();
  const [heightSwitcher, setHeightSwitcher] = useState<number>(0);
  const RFW_CONTAINER_HEIGHT = 60;
  const navigateWithSearch = useNavigateWithSearch();
  const rootPath = useCurrentRootPath();

  const limit = 20;
  const [page, setPage] = useState<number>(1);
  const listGroupId = activeGroupName
    ? spanNetworkGroup?.id
    : selectedNetworkGroup?.id;

  const { isLoading, isFetching, data, originalArgs } =
    useFilterNetworkGroupAnalysisQuery(
      listGroupId === undefined ||
        !selectedNetworkGroup?.id ||
        !selectedAnalysis
        ? skipToken
        : {
            analysis_type: selectedAnalysis,
            active_group_id: selectedNetworkGroup?.id,
            active_group_name: activeGroupName,
            group_id: listGroupId,
            limit,
            page,
            query: filterQuery,
          }
    );

  // Group-level Summary view: desktop renders Summary/Spans tabs above the
  // active group, mobile swaps GroupListComponent for GroupRiskSummary when
  // the user picks the Summary segment in the toggle. Both share the same
  // request, keyed on the parent (circuit-level) group and looked up by
  // `activeGroupName`.
  // Summary/Spans tabs only appear for Wildfire Intelligence customers (WPL off).
  // WPL customers see the legacy span list on drill-in.
  const wildfireGroupActive =
    activeGroupName != null &&
    selectedAnalysis === GroupAnalysisType.WILDFIRE_PRIORITY &&
    hasGroupRiskSummary;
  const showSummaryTabs = !isSmallScreen && wildfireGroupActive;
  const showMobileSummary =
    isSmallScreen &&
    isListView &&
    wildfireGroupActive &&
    groupDetailTab === 'summary';
  const {
    data: groupSummaryByName,
    isLoading: isSummaryLoading,
    isFetching: isSummaryFetching,
  } = useFilterGroupSummaryAnalysisQuery(
    {
      group_id: selectedNetworkGroup?.id ?? 1,
      analysis_type: selectedAnalysis,
      query: filterQuery,
    },
    {
      skip:
        (!showSummaryTabs && !showMobileSummary) || !selectedNetworkGroup?.id,
    }
  );
  const activeGroupSummary = activeGroupName
    ? groupSummaryByName?.get(activeGroupName)
    : undefined;

  const handleAddToList = useAddToList({
    total: data?.summary?.total,
    openModal,
  });

  // Default the Summary/Spans tab back to Summary whenever the active group
  // (re-)mounts.
  useEffect(() => {
    if (activeGroupName != null) {
      dispatch(setGroupDetailTab('summary'));
    }
  }, [activeGroupName, dispatch]);

  useEffect(() => {
    dispatch(
      setEncroachmentThresholds(
        data?.summary?.categoriesByEncroachmentScore ??
          DEFAULT_CATEGORY_THRESHOLD
      )
    );
    dispatch(
      setPriorityThresholds(
        data?.summary?.categoriesByPriorityScore ?? DEFAULT_CATEGORY_THRESHOLD
      )
    );
    dispatch(
      setWildfirePriorityThresholds(
        (hasGroupRiskSummary
          ? data?.summary?.categoriesByWildfireRisk
          : data?.summary?.categoriesByWildfirePriorityScore) ??
          DEFAULT_CATEGORY_THRESHOLD
      )
    );
    dispatch(
      setCriticalTreesThresholds(
        data?.summary?.categoriesByCriticalTreesCount ??
          DEFAULT_CATEGORY_THRESHOLD
      )
    );
    dispatch(
      setClearThresholds(
        data?.summary?.categoriesByClear ?? DEFAULT_CATEGORY_THRESHOLD
      )
    );

    if (data?.summary.bbox && !isFetching) {
      dispatch(setFilterBoundingBox(data.summary.bbox));
      const prevFilterQuery = filterQuery.previousFilterQuery as FilterState;
      const shouldUpdate = filterStateChangeShouldTriggerUpdateViewState(
        filterQuery,
        prevFilterQuery
      );
      if (shouldUpdate) {
        const updatedViewState = updateViewport(
          viewState,
          data.summary.bbox,
          width,
          DESKTOP_VIEWBOX_PADDING,
          MOBILE_VIEWBOX_PADDING
        );
        dispatch(updateViewState(updatedViewState));
      }
    }
    // This is a legacy dependency list.
    // We have kept it to avoid breaking existing functionality,
    // but it may be a source of bugs in the future.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.summary, dispatch, hasGroupRiskSummary]);

  useEffect(() => {
    const switcherHeight = switcher.current?.clientHeight;
    if (switcherHeight && heightSwitcher !== switcherHeight) {
      setHeightSwitcher(
        shouldShowRedFlagWarnings
          ? switcherHeight + RFW_CONTAINER_HEIGHT
          : switcherHeight
      );
    }
  }, [
    isListView,
    selectedAnalysis,
    activeGroupName,
    switcher.current?.clientHeight,
    heightSwitcher,
    shouldShowRedFlagWarnings,
  ]);

  const spanNetworkGroupRef = useRef(spanNetworkGroup?.id);
  const initialNetworkGroupSet = useRef(false);

  /**
   * Reference to track the first update.
   * We don't want to clear the item when selectedNetworkGroup is first populated
   */
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (
      selectedNetworkGroup?.id !== spanNetworkGroup?.id &&
      window.location.pathname !== ''
    ) {
      if (initialNetworkGroupSet.current) {
        dispatch(clearItem());
      } else if (spanNetworkGroupRef.current) {
        // Only set if the span network group name is already populated
        initialNetworkGroupSet.current = true;
      }
    }
    // Update refs with the latest values inside the effect
    spanNetworkGroupRef.current = spanNetworkGroup?.id;
  }, [selectedNetworkGroup?.id, spanNetworkGroup?.id, dispatch]);

  useEffect(() => {
    if (isSmallScreen) {
      dispatch(setListView(false));
    }
  }, [isSmallScreen, dispatch]);

  const handleLevel2ItemClick = (
    getUnitBbox: (v: WebMercatorViewportOptions) => WebMercatorViewportOptions,
    groupName: string
  ) => {
    const newBbox = getUnitBbox(viewState);
    // Snapshot the current viewport so the in-app Back button can restore it.
    dispatch(saveViewStateBeforeDrill(viewState));
    dispatch(setActiveGroupName(groupName));
    dispatch(updateViewState(newBbox));

    // GA event for level2
    TagManager.dataLayer({
      dataLayer: {
        event: GoogleEvents.level2Clicked,
      },
    });
  };

  useEffect(() => {
    if (!isSmallScreen) {
      dispatch(setListView(true));
    }
  }, [isSmallScreen, dispatch]);

  const filterStateChangeShouldTriggerUpdateViewState = (
    currFilter: FilterState,
    prevFilter: FilterState
  ): boolean => {
    /**
     * Some changes to filterQuery state (groups and network types) should trigger an update
     * to the view (to recenter and resize to the newly filtered sets' bounding box)
     */
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return true;
    }

    // Check Group filters for change
    // Massage group filters to remove elements whose value is undefined
    const cleanCurrGroups = filterFalsyValues(currFilter.groups);
    const cleanPrevGroups = filterFalsyValues(prevFilter.groups);
    const groupFiltersDiff =
      Object.keys(cleanCurrGroups).length !==
        Object.keys(cleanPrevGroups).length ||
      !isEqual(cleanCurrGroups, cleanPrevGroups);

    // Check Network Type filters for change
    const networkTypeFiltersDiff = !isEqual(
      currFilter.network,
      prevFilter.network
    );

    const redflagFiltersDiff = !isEqual(
      currFilter.redFlagWarnings,
      prevFilter.redFlagWarnings
    );

    return groupFiltersDiff || networkTypeFiltersDiff || redflagFiltersDiff;
  };

  /**
   * We have some...nuanced...list height requirements.
   */
  const listHeight = useListHeight({
    isSmallScreen,
    isListView,
  });

  const listSummary = (
    <ListSummary
      unitCount={data?.summary?.total || 0}
      distance={data?.summary?.totalLength || 0}
      isLoading={isLoading || isFetching}
    />
  );

  const bulkActionDropdown = (
    <BulkActionDropdown
      onSpanExport={() => setShowExportModal(true)}
      onCircuitExport={() => setShowExportModal(true)}
      onAddToList={handleAddToList}
      onAddActivity={() =>
        dispatch(
          openWorklogModal({
            mode: WorklogModalMode.BULK_ADD,
            filter: filterQuery,
            spanCount: data?.summary?.total || 0,
          })
        )
      }
    />
  );

  const showBulkActions = useShouldShowBulkActions();
  const toggleRow = (isSmallScreen || showBulkActions) && (
    <Group
      justify="space-between"
      data-testid="map-list-toggle-row"
      px={isSmallScreen ? 'md' : undefined}
      pt={isSmallScreen ? 'xs' : undefined}
      pb={isSmallScreen ? 'xxs' : undefined}
    >
      <Box>{showBulkActions && bulkActionDropdown}</Box>
      {isSmallScreen && <MapListToggle />}
    </Group>
  );

  // Body of the list pane below the toggle. Three shapes:
  //   * mobile + Summary segment selected → just GroupRiskSummary
  //   * desktop + drilled into a wildfire group → Summary/Spans tabs
  //     gating the same GroupRiskSummary or GroupListComponent
  //   * everything else → GroupListComponent
  const groupListComponent = (
    <GroupListComponent
      setShowExportModal={setShowExportModal}
      listRef={listRef}
      onClick={handleLevel2ItemClick}
      dataProps={{
        isLoading,
        isFetching,
        data,
        originalArgs,
        limit,
        page,
        setPage,
      }}
    />
  );

  const summaryPanel = activeGroupSummary ? (
    <GroupRiskSummary summary={activeGroupSummary} />
  ) : isSummaryLoading || isSummaryFetching ? (
    <Box p="md" data-testid="group-risk-summary-loading">
      <Text>{intl.formatMessage({ id: 'action.loading' })}</Text>
    </Box>
  ) : (
    <Box p="md" data-testid="group-risk-summary-empty" />
  );

  let groupDetailContent: ReactNode = groupListComponent;

  if (showMobileSummary) {
    groupDetailContent = summaryPanel;
  } else if (showSummaryTabs) {
    const tabOptions = {
      summary: {
        label: intl.formatMessage({ id: 'analysisList.tab.summary' }),
        event: GoogleEvents.openNetworkGroupSummaryTab,
      },
      spans: {
        label: intl.formatMessage({ id: 'analysisList.tab.spans' }),
        event: GoogleEvents.openNetworkGroupSpansTab,
      },
    } as const satisfies Record<
      GroupDetailTab,
      { label: string; event: string }
    >;
    groupDetailContent = (
      <div data-testid="group-detail-tabs" className={classes.groupDetailTabs}>
        <TabsComponent
          variant="drawer"
          activeTab={tabOptions[groupDetailTab].label}
          setActiveTab={(label) => {
            const key = (Object.keys(tabOptions) as GroupDetailTab[]).find(
              (tab) => tabOptions[tab].label === label
            );
            if (key && key !== groupDetailTab) {
              dispatch(setGroupDetailTab(key));
              TagManager.dataLayer({
                dataLayer: { event: tabOptions[key].event },
              });
            }
          }}
          tabs={[
            { header: tabOptions.summary.label, Component: summaryPanel },
            { header: tabOptions.spans.label, Component: groupListComponent },
          ]}
        />
      </div>
    );
  }

  return (
    <ListPane
      listWrapperStyles={{
        // If the screen is small and the list view is active, override the zIndex
        // that's set in the CSS Module
        zIndex: isSmallScreen && isListView ? 1 : undefined,
      }}
      listStyles={{
        height: listHeight,
      }}
      className={classes.listPane}
      isLoaded={!isLoading && !isFetching}
    >
      <div ref={switcher} className={classes.switcher}>
        {isSmallScreen && !activeGroupName && (
          <ObjectiveDropdown layerConfig={layerConfig} showGroupBy />
        )}
        {isSmallScreen && activeGroupName && (
          <>
            <Flex
              align="center"
              justify="space-between"
              px="md"
              className={classes.mobileNavRow}
            >
              <UnstyledButton
                className={classes.backButton}
                onClick={() => {
                  dispatch(setActiveGroupName(null));
                  dispatch(clearItem());
                  dispatch(restoreViewStateBeforeDrill());
                  navigateWithSearch(rootPath);
                }}
              >
                <ChevronLeft size={16} />
                {intl.formatMessage({ id: 'action.back' })}
              </UnstyledButton>
              <ObjectiveDropdown
                layerConfig={layerConfig}
                showGroupBy={false}
                compact
              />
            </Flex>
            <Stack px="md" pb={4} gap="xxs">
              <Title
                order={4}
                lineClamp={2}
                style={theme.other.typography.variants.h4}
              >
                {activeGroupName}
              </Title>
              {listSummary}
            </Stack>
          </>
        )}
        {!isSmallScreen && activeGroupName && (
          <>
            <DetailViewHeader
              heading={activeGroupName}
              onBack={() => {
                dispatch(setActiveGroupName(null));
                dispatch(clearItem());
                dispatch(restoreViewStateBeforeDrill());
                navigateWithSearch(rootPath);
              }}
            />
            {listSummary}
          </>
        )}

        {!activeGroupName && !isSmallScreen && (
          <Stack gap="xxs">{listSummary}</Stack>
        )}
        {toggleRow}
      </div>

      {isListView && (
        <Stack
          ref={listStackRef}
          className={classes.listStack}
          data-testid="list-stack"
          data-scrolled={isListStackScrolled || undefined}
        >
          {isSmallScreen && !showMobileSummary && (
            <FeatureBar
              shouldShowRedFlagWarnings={shouldShowRedFlagWarnings}
              setRedFlagWarnings={setRedFlagWarnings}
            />
          )}
          {isSmallScreen && !activeGroupName && (
            <Box px="md" style={{ flexShrink: 0 }}>
              {listSummary}
            </Box>
          )}
          <AnalysisExport
            showExportModal={showExportModal}
            onClose={() => setShowExportModal(false)}
            selectedAnalysis={selectedAnalysis}
          />
          {groupDetailContent}
        </Stack>
      )}
      {modalComponent}
    </ListPane>
  );
};
